import { auth } from "@/plugins/firebase";
import { RootState, User, UserState } from "@/store/types";
import { ActionTree, GetterTree, Module, MutationTree } from "vuex";

const AnonymousUser = Object.freeze({
  uid: "",
  idToken: "",
  claims: { admin: false },
  displayName: "",
  email: "",
  isAnonymous: true,
  phoneNumber: "",
  photoURL: ""
} as User);

const mutations: MutationTree<UserState> = {
  setUser(state, payload: User) {
    state.user = payload;
    state.userLoaded = true;
  }
};

const actions: ActionTree<UserState, RootState> = {
  init(context) {
    auth.onAuthStateChanged(async user => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult(true);
        context.commit(
          "setUser",
          Object.freeze({
            ...user,
            idToken: idTokenResult.token,
            claims: idTokenResult.claims
          })
        );
        await context.dispatch("files/bindFiles", null, {
          root: true
        });
      } else {
        context.commit("setUser", Object.freeze({ ...AnonymousUser }));
      }
    });
  },
  waitForUserLoaded(context) {
    return new Promise(resolve => {
      if (context.state.userLoaded) {
        resolve(true);
      } else {
        const unwatch = this.watch(
          (state, getters) => getters["users/userHasLoaded"],
          newValue => {
            if (newValue) {
              resolve(true);
              unwatch();
            }
          }
        );
      }
    });
  },
  async logOut(context) {
    await auth.signOut();
    context.commit("setUser", Object.freeze({ ...AnonymousUser }));
  }
};

const getters: GetterTree<UserState, RootState> = {
  fullName(state): string {
    const { user } = state;
    return user.displayName;
  },
  userRefPath(state) {
    return (userUid: string | undefined) => {
      const uid = userUid || state.user.uid;
      return `/users/${uid}`;
    };
  },
  isAuthenticated(state): boolean {
    const { user } = state;
    return !user.isAnonymous;
  },
  isAdmin(state): boolean {
    const { user } = state;
    return user.claims.admin;
  },
  userHasLoaded(state): boolean {
    return state.userLoaded;
  },
  avatarUrl(state): string {
    const { user } = state;
    return user.photoURL;
  }
};

const users: Module<UserState, RootState> = {
  namespaced: true,
  state: {
    user: {
      ...AnonymousUser
    },
    userLoaded: false
  },
  mutations: mutations,
  actions: actions,
  getters: getters
};

export default users;
